import { Injectable, NgModuleRef } from '@angular/core';
import { CsModal, Modal, ModalInstance, ModalVoidCallback } from '@cargos/cs-modal';
import moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, mergeMap, take } from 'rxjs/operators';
import { LegalTerms } from 'src/app/utils/cargo-driver-check-in-model';
import { LegalTermsNotificationMode } from 'src/app/utils/cargo-driver-check-in-model/legal-terms';
import { LegalTermsService } from 'src/app/utils/cargo-driver-check-in-service/services';
import { AcceptanceLegalTermsComponent } from '../acceptance-legal-terms.component';
import { AcceptanceLegalTermsResult } from '../models/acceptance-legal-terms-result';

@Injectable({
  providedIn: 'root'
})
export class AcceptanceLegalTermsService {
  private csModal: CsModal;
  get entryFlagRecentLogin(): string {
    return `p4ss${+moment}recentLogin`;
  }
  private topNoticeBarVisible = new BehaviorSubject<boolean>(false);

  constructor(
    private legalTermsService: LegalTermsService,
    private ngModuleRef?: NgModuleRef<unknown>
  ) {
    this.csModal = ModalInstance.generate({});
  }

  getSettings(): Observable<Modal> {
    return this.csModal.getSettings();
  }

  private launch(legalTerms: LegalTerms): Observable<AcceptanceLegalTermsResult> {
    return new Observable<AcceptanceLegalTermsResult>(subscriber => {
      let close: boolean | ModalVoidCallback<AcceptanceLegalTermsComponent> = false;

      if (legalTerms.ltm_notification_mode === LegalTermsNotificationMode.INFORMATIVE) {
        subscriber.next(AcceptanceLegalTermsResult.INFORMATIONAL_VISIBLE);

        close = (modal: Modal<AcceptanceLegalTermsComponent>) => {
          subscriber.next(AcceptanceLegalTermsResult.DISMISSED);
          subscriber.complete();
          modal.close();
        };
      }

      this.csModal.confirm<AcceptanceLegalTermsComponent>({
        content: {
          componentClass: AcceptanceLegalTermsComponent,
          options: {
            ngModuleRef: this.ngModuleRef
          }
        },
        columnClass: 'medium',
        fontSize: 'medium',
        contentAlign: 'center',
        buttonsAlign: 'center',
        animation: 'scale',
        onContentReady: (modal: Modal<AcceptanceLegalTermsComponent>) => {
          const contentInstance = modal.contentInstance;
          contentInstance.init(legalTerms);

          contentInstance.action
            .pipe(
              take(1)
            )
            .subscribe(result => {
              subscriber.next(result);
              subscriber.complete();
              modal.close();
            });
        },
        escapeKey: close,
        closeIcon: close,
        backgroundDismiss: close
      });

      return {
        unsubscribe: () => { }
      };
    });
  }

  launchIfApplicable(): Observable<AcceptanceLegalTermsResult> {
    return this.legalTermsService.getLegalTermsByCurrentUser()
      .pipe(
        take(1),
        mergeMap(legalTerms => {
          this.hideTopNoticeBar();

          if (!legalTerms) {
            return of(AcceptanceLegalTermsResult.NOT_APPLICABLE);
          }

          if (legalTerms.ltm_notification_mode === LegalTermsNotificationMode.INFORMATIVE) {
            this.showTopNoticeBar();
            return of(AcceptanceLegalTermsResult.INFORMATIONAL_VISIBLE);
          }

          return this.launch(legalTerms);
        }),
        catchError(error => {
          console.error(error);

          return of(AcceptanceLegalTermsResult.NOT_APPLICABLE);
        })
      );
  }

  showTopNoticeBar(): void {
    this.topNoticeBarVisible.next(true);
  }

  hideTopNoticeBar(): void {
    this.topNoticeBarVisible.next(false);
  }

  isTopNoticeBarVisible(): Observable<boolean> {
    return this.topNoticeBarVisible.asObservable()
      .pipe(
        distinctUntilChanged()
      );
  }

  saveFlagRecentLogin(): void {
    sessionStorage.setItem(atob(`${this.entryFlagRecentLogin}`), 'true');
  }

  isRecenteLogin(): boolean {
    return !!sessionStorage.getItem(atob(`${this.entryFlagRecentLogin}`));
  }

  clearFlagRecenteLogin(): void {
    sessionStorage.removeItem(atob(`${this.entryFlagRecentLogin}`))
  }

}
